import { Layout, Modal, theme } from "antd";
import { lazy, Suspense, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate, matchPath } from "react-router-dom";
import { AuthContext } from "../../providers/AuthProvider";
import {
  hideAccessDeniedModal,
  hideSessionExpiredModal,
  setOpenSearchBar,
} from "../../redux/slices/authSlice";
import GlobalSearch from "../GlobalSearch";
import SidePannel from "./SidePannel";
import { Skeleton } from "antd";

const Forbidden = lazy(() => import("../../pages/auth/Forbidden"));

const { Header, Content, Sider } = Layout;

const ProtectedLayout = () => {
  const [collapsed, setCollapsed] = useState(true);
  const { openSearchBar } = useSelector((state) => state.search);

  const {
    token: { colorBgContainer },
  } = theme.useToken();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { authState, isAuthenticated } = useContext(AuthContext);

  const showSessionModal = useSelector((store) => store.auth.sessionExpired);
  const showAccessDenied = useSelector((store) => store.auth.accessDenied);
  const { accessRoutes } = useSelector((store) => store.auth);
  const location = useLocation();
  const path = location.pathname;

  const isAuthorized =
    accessRoutes.length === 0 ||
    accessRoutes.some((route) => matchPath({ path: route, end: false }, path));
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const handler = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        dispatch(setOpenSearchBar(true));
      }
    };
    window.addEventListener("keydown", handler);

    return () => window.removeEventListener("keydown", handler);
  }, [dispatch]);

  const handleOk = () => {
    dispatch(hideSessionExpiredModal());
    navigate("/login", { replace: true });
  };

  return (
    <Layout>
      {/* Side Panel */}
      <SidePannel collapsed={collapsed} setCollapsed={setCollapsed} />

      {/* Main Layout Content */}
      <Layout
        style={{
          zIndex: 0,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          minHeight: "100vh",
          marginLeft: "3.85rem",
        }}
      >
        {openSearchBar && <GlobalSearch />}
        <Content
          style={{
            zIndex: 0,
          }}
        >
          {isAuthorized ? (
            <Outlet />
          ) : (
            <Suspense fallback={<Skeleton active style={{ margin: "10px 10px", width: "90vw" }} />}>
              <Forbidden isUnauthorized={true} />
            </Suspense>
          )}
        </Content>
      </Layout>

      {/* Session Expired Modal */}
      <Modal
        title="Session Expired"
        open={showSessionModal}
        onOk={handleOk}
        cancelButtonProps={{ style: { display: "none" } }}
      >
        <p>Your session has expired. Please log in again.</p>
      </Modal>

      {/* Access Denied Modal */}
      <Modal
        title="Access Denied"
        open={showAccessDenied}
        onOk={() => dispatch(hideAccessDeniedModal())}
        onCancel={() => dispatch(hideAccessDeniedModal())}
        okText="Got it"
        cancelButtonProps={{ style: { display: "none" } }}
        centered
        type="warning"
        style={{ zIndex: 1000 }}
      >
        <p>You do not have permission to perform this action. Contact Admin.</p>
      </Modal>
    </Layout>
  );
};

export default ProtectedLayout;
